import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { unstable_useBlocker as useBlocker } from 'react-router';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { showNotificationAsync } from '../../../data-layer/notifications/actions';
import {
    removePostsAsync,
    updatePostBoostingAsync,
    updatePostStatusAsync,
} from '../../../data-layer/posts/actions';
import { useDispatch } from '../../../data-layer/useDispatch';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { FileLoadingNotification } from '../../../ui/business/FileLoadingNotification/FileLoadingNotification';
import { Alert, confirmAlert } from '../../../ui/feedback/Alert';
import { Button } from '../../../ui/input/Button';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { InfiniteList } from '../../../ui/layout/InfiniteList';
import { StickyFooter } from '../../../ui/layout/StickyFooter';
import { FeedManagementFilters } from '../../../ui/posts/FeedManagement/parts/FeedManagementFilters/FeedManagementFilters';
import { ManagementFiltersParams } from '../../../ui/posts/FeedManagement/parts/FeedManagementFiltersDrawer/types';
import { FeedManagementHeader } from '../../../ui/posts/FeedManagement/parts/FeedManagementHeader/FeedManagementHeader';
import { FeedManagementRow } from '../../../ui/posts/FeedManagement/parts/FeedManagementRow/FeedManagementRow';
import { FeedManagementRowSkeleton } from '../../../ui/posts/FeedManagement/parts/FeedManagementRowSkeleton/FeedManagementRowSkeleton';
import { FeedManagementAdditionalField } from '../../../ui/posts/FeedManagement/types';
import { PostContextMenu } from '../../../ui/posts/PostContextMenu';
import { PostStatus } from '../../../ui/posts/constants';
import { Typography } from '../../../ui/typography/Typography';
import { hasAccessToFeedManagement } from '../../../utils/accessService';
import { EventSource } from '../../../utils/analyticsService';
import {
    ymTrackFeedManager,
    ymTrackFeedManagerBulkChangeStatus,
    ymTrackFeedManagerChangeFiltersParams,
    ymTrackFeedManagerSelectPost,
} from '../../../utils/metrika';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useIsContentPromotionEnabled } from '../../../utils/useIsContentPromotionEnabled';
import { useMeta } from '../../../utils/useMeta';
import { useTranslation } from '../../../utils/useTranslation';
import { usePostCollection } from '../../post/usePostCollection';
import { withPostCallbacks } from '../../post/withPostCallbacks';
import { useCurrentUser } from '../../users/useCurrentUser';
import { withBusinessAuthProtection } from '../../withBusinessAuthProtection';
import { BusinessContentPromotionDetails } from '../BusinessContentPromotionDetails';
import {
    BORDER_RADIUS,
    BULK_CONFIRM_MESSAGE_MAPPING,
    BULK_SUCCESS_MESSAGE_MAPPING,
    LIMIT,
    QUERY_PARAMETER_SOURCE,
    QUERY_PARAMETER_STATUS,
    QUERY_PARAMETER_PERIOD,
    QUERY_PARAMETER_DATE_FROM,
    QUERY_PARAMETER_DATE_TO,
} from './constants';
import { useExportStatistic } from './hooks/useExportStatistic';
import { IdentifierDictionary } from './types';
import {
    canChangeStatusBulk,
    isAllSelected,
    makeFilters,
    makeIdentifiers,
    makeMapping,
    makeSearchParams,
} from './utils';

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

export const FeedManagement = withBusinessAuthProtection(() => {
    const t = useTranslation();
    const { businessId } = useParams();
    const [query, setQuery] = useSearchParams();
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const [selected, setSelected] = useState<IdentifierDictionary>({});
    const identifiers = makeIdentifiers(selected);
    const collection = usePostCollection({
        namespace: 'management',
        filters: {
            businessId,
            amount: LIMIT,
            ...makeFilters(query),
        },
    });
    const contentPromotionEnabled = useIsContentPromotionEnabled();

    // Обработчик изменения тумблера продвижения
    const onPromotionToggle = useCallback(
        async (postId: string, enabled: boolean) => {
            await dispatch(updatePostBoostingAsync(postId, enabled));
        },
        [dispatch],
    );

    const onBulkChangeStatus = useCallback(
        async (status: PostStatus) => {
            ymTrackFeedManagerBulkChangeStatus(status, {
                page: 'Управления постами',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            try {
                await confirmAlert('Подтверждение', BULK_CONFIRM_MESSAGE_MAPPING[status]!);
            } catch (error) {
                return;
            }

            const succeed = await dispatch(
                status === PostStatus.DELETED
                    ? removePostsAsync(...identifiers)
                    : updatePostStatusAsync(identifiers, status, currentUser),
            );

            if (succeed) {
                const message = BULK_SUCCESS_MESSAGE_MAPPING[status];

                if (message) {
                    dispatch(showNotificationAsync(message));
                }

                setSelected({});
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            dispatch,
            selected,
            setSelected,
            currentUser?.role,
            currentUser?.profile?.publicId,
            currentUser?.login,
        ],
    );

    const onSelectAll = useCallback(() => {
        if (isAllSelected(collection.items, identifiers)) {
            return setSelected({});
        }

        setSelected(makeMapping(collection.items));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, collection.items, setSelected]);

    const onSelect = useCallback(
        (postId: string, value: boolean) => {
            ymTrackFeedManagerSelectPost({
                page: 'Управления постами',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            setSelected({ ...selected, [postId]: value });
        },
        [selected, setSelected, currentUser?.login, currentUser?.profile?.publicId],
    );

    const unloadGuard = useBlocker(
        (context) =>
            context.nextLocation.pathname !== context.currentLocation.pathname &&
            Boolean(identifiers.length > 0),
    );

    useEffectOnce(() => () => collection.onReset());

    useMeta({ title: 'Управление лентой • Яндекс Ритм' });

    if (!currentUser) {
        return null;
    }

    if (!businessId || !hasAccessToFeedManagement(businessId, currentUser)) {
        return <Navigate to="/" replace />;
    }

    useEffect(() => {
        ymTrackFeedManager({
            page: 'Управления постами',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goalParams = {
        page: 'Управления постами',
        userLogin: currentUser?.login,
        businessId: currentUser?.profile?.publicId,
    };

    const makeChangeHandler = (name: string) => (value: string | null) => {
        setQuery((searchParams) => {
            ymTrackFeedManagerChangeFiltersParams(name, goalParams);

            makeSearchParams(searchParams, [{ name, value }]);

            collection.onUpdateQuery(makeFilters(searchParams));
            setSelected({});

            return searchParams;
        });
    };

    const onChangeDate = (period: string | null, dateFrom?: string, dateTo?: string) => {
        setQuery((searchParams) => {
            ymTrackFeedManagerChangeFiltersParams(QUERY_PARAMETER_PERIOD, goalParams);

            makeSearchParams(searchParams, [
                { name: QUERY_PARAMETER_PERIOD, value: period },
                { name: QUERY_PARAMETER_DATE_FROM, value: dateFrom },
                { name: QUERY_PARAMETER_DATE_TO, value: dateTo },
            ]);

            collection.onUpdateQuery(makeFilters(searchParams));
            setSelected({});

            return searchParams;
        });
    };

    const onChangeFilters = ({
        source,
        status,
        period,
        dateFrom,
        dateTo,
    }: ManagementFiltersParams) => {
        setQuery((searchParams) => {
            ymTrackFeedManagerChangeFiltersParams(QUERY_PARAMETER_SOURCE, goalParams);
            ymTrackFeedManagerChangeFiltersParams(QUERY_PARAMETER_STATUS, goalParams);
            ymTrackFeedManagerChangeFiltersParams(QUERY_PARAMETER_PERIOD, goalParams);

            makeSearchParams(searchParams, [
                { name: QUERY_PARAMETER_SOURCE, value: source },
                { name: QUERY_PARAMETER_STATUS, value: status },
                { name: QUERY_PARAMETER_PERIOD, value: period },
                { name: QUERY_PARAMETER_DATE_FROM, value: dateFrom },
                { name: QUERY_PARAMETER_DATE_TO, value: dateTo },
            ]);

            collection.onUpdateQuery(makeFilters(searchParams));
            setSelected({});

            return searchParams;
        });
    };

    const [additionalFields, setAdditionalFields] = useState<Record<string, boolean>>(
        contentPromotionEnabled
            ? {
                  [FeedManagementAdditionalField.PROMOTION]: true,
              }
            : {},
    );

    const onChangeMenu = useCallback((values: string[]) => {
        const newFields = values.reduce((res, item) => ({ ...res, [item]: true }), {});

        setAdditionalFields(newFields);
    }, []);

    const publishButton = canChangeStatusBulk(
        collection.items,
        identifiers,
        PostStatus.MODERATION,
        currentUser,
    ) && (
        <Button
            color="white"
            flexGrow={1}
            onClick={() => onBulkChangeStatus(PostStatus.MODERATION)}
        >
            {t('Опубликовать')} ({identifiers.length})
        </Button>
    );
    const deleteButton = canChangeStatusBulk(
        collection.items,
        identifiers,
        PostStatus.DELETED,
        currentUser,
    ) && (
        <Button color="white" flexGrow={1} onClick={() => onBulkChangeStatus(PostStatus.DELETED)}>
            {t('Удалить')} ({identifiers.length})
        </Button>
    );

    const enableExportStat = useExperimentFlag('export_statistic_xlsx');
    const { isLoadingStatFile, onExportClick } = useExportStatistic(
        businessId,
        query,
        additionalFields,
    );

    return (
        <>
            {contentPromotionEnabled ? <BusinessContentPromotionDetails /> : null}
            <Flex
                borderRadius={2}
                background="var(--background-primary)"
                width="100%"
                overflow="clip"
            >
                <FeedManagementFilters
                    sourceValue={query.get(QUERY_PARAMETER_SOURCE)}
                    onChangeSource={makeChangeHandler('source')}
                    statusValue={query.get(QUERY_PARAMETER_STATUS)}
                    onChangeStatus={makeChangeHandler('status')}
                    periodValue={query.get(QUERY_PARAMETER_PERIOD)}
                    dateFrom={query.get(QUERY_PARAMETER_DATE_FROM)}
                    dateTo={query.get(QUERY_PARAMETER_DATE_TO)}
                    onChangeDate={onChangeDate}
                    menuValues={Object.keys(additionalFields)}
                    onChangeMenu={onChangeMenu}
                    onChangeFilters={onChangeFilters}
                    onExportClick={onExportClick}
                />
                {enableExportStat && isLoadingStatFile
                    ? createPortal(<FileLoadingNotification />, document.body)
                    : null}
                {collection.isLoading || collection.items.length > 0 ? (
                    <Box
                        width="100%"
                        background="var(--background-primary)"
                        borderBottomLeftRadius={BORDER_RADIUS}
                        borderBottomRightRadius={BORDER_RADIUS}
                        borderTop="1px solid var(--divider)"
                        pb={identifiers.length > 0 ? 7 : 0}
                        position="relative"
                    >
                        <Box overflowX="auto">
                            <FeedManagementHeader
                                selected={isAllSelected(collection.items, identifiers)}
                                onSelect={onSelectAll}
                                position="sticky"
                                top={0}
                                background="var(--background-primary)"
                                additionalFields={additionalFields}
                                borderTop="none"
                            />
                            <InfiniteList
                                perPage={3}
                                count={collection.items.length}
                                isLoading={collection.isLoading}
                                isEndReached={collection.isEndReached}
                                rowHeight={() => 80}
                                estimatedRowSize={() => 80}
                                onLoadNext={collection.onLoadNext}
                                style={{ overflowX: 'visible', overflowY: 'visible' }}
                                containerStyle={{ overflow: 'visible' }}
                                autoRowWidth
                                renderItem={(index) => {
                                    const model = collection.items[index];

                                    if (model) {
                                        return (
                                            <FeedManagementRow
                                                selected={selected[model.id]}
                                                post={toPostUI(model)}
                                                renderMenu={(trigger) => (
                                                    <EnhancedPostContextMenu
                                                        index={index}
                                                        trigger={trigger}
                                                        post={model}
                                                        user={currentUser}
                                                        fromBusinessId={businessId}
                                                        fromManagement
                                                        returnFocusOnDeactivate={false}
                                                    />
                                                )}
                                                onSelect={(value) => onSelect(model.id, value)}
                                                onPromotionToggle={onPromotionToggle}
                                                additionalFields={additionalFields}
                                                minWidth="100%"
                                            />
                                        );
                                    }

                                    return <FeedManagementRowSkeleton />;
                                }}
                            />
                        </Box>
                        <Box
                            position="absolute"
                            top={0}
                            right={-1}
                            bottom={0}
                            width={40}
                            background="linear-gradient(to left, var(--background-primary), transparent 100%)"
                            pointerEvents="none"
                        />
                    </Box>
                ) : (
                    <Typography
                        variant="body"
                        weight="medium"
                        color="secondary"
                        p={3}
                        borderTop="1px solid var(--divider)"
                        width="100%"
                        textAlign="center"
                    >
                        Ничего не найдено 👀
                    </Typography>
                )}
                {publishButton || deleteButton ? (
                    <StickyFooter
                        style={{
                            transform:
                                identifiers.length > 0
                                    ? 'translateY(calc(-100% + 1px))'
                                    : 'translateY(0)',
                        }}
                    >
                        {publishButton}
                        {deleteButton}
                    </StickyFooter>
                ) : null}
                {unloadGuard.state === 'blocked' && (
                    <Alert
                        title="Переход"
                        description="Все выбранные посты будут сброшены. Продолжить?"
                        actions={[
                            {
                                label: 'Перейти',
                                onClick: () => unloadGuard.proceed?.(),
                            },
                            {
                                label: 'Остаться',
                                variant: 'filled',
                                onClick: () => unloadGuard.reset?.(),
                            },
                        ]}
                    />
                )}
            </Flex>
        </>
    );
});
